import { Link } from "react-router";
import { Adjuntos, Alerta, Badge, Card, DataList, Desplegable, Row, Spinner, Stack } from "@/components";
import type { PagoCompraTerreno } from "@/lib/api";
import { formatFecha, formatMoneda, formatNumero } from "@/lib/format";
import { ETIQUETA_ESTADO, TONO_ESTADO } from "@/lib/gastos";
import { useCompraTerrenoTab } from "./useCompraTerrenoTab";

function Dato({ etiqueta, valor }: { etiqueta: string; valor: string }) {
  return (
    <div className="contents">
      <dt className="text-texto-suave">{etiqueta}</dt>
      <dd className="break-words">{valor}</dd>
    </div>
  );
}

/** La compra del terreno como un gasto más (Mati, 2026-10-28): el precio acordado, los pagos a cuenta y lo que falta. */
export function CompraTerrenoTab() {
  const t = useCompraTerrenoTab();
  const c = t.compra;

  if (t.cargando) {
    return (
      <div className="grid place-items-center py-10 text-primario">
        <Spinner />
      </div>
    );
  }
  if (t.errorCarga || !c) {
    return (
      <Stack espacio={3}>
        <Alerta texto={t.errorCarga ?? "Todavía no se cargó el precio acordado del terreno"} />
        <Link to="/gastos" className="text-primario underline-offset-2 hover:underline">
          Volver a gastos
        </Link>
      </Stack>
    );
  }

  const g = c.gasto;
  const columnas = [
    { clave: "fecha", titulo: "Fecha", render: (p: PagoCompraTerreno) => formatFecha(p.fecha) },
    { clave: "concepto", titulo: "Concepto", render: (p: PagoCompraTerreno) => <span className="font-medium">{p.concepto}</span> },
    { clave: "monto", titulo: "Monto", render: (p: PagoCompraTerreno) => formatMoneda(p.monto, p.moneda) },
    { clave: "tc", titulo: "Tipo de cambio", render: (p: PagoCompraTerreno) => (p.tipo_cambio ? formatNumero(p.tipo_cambio) : "—") },
    { clave: "usd", titulo: "En USD", render: (p: PagoCompraTerreno) => formatMoneda(p.monto_usd, "USD") },
    { clave: "quien", titulo: "Lo pagó", render: (p: PagoCompraTerreno) => p.pagado_por ?? "El fideicomiso" },
  ];

  return (
    <Stack espacio={6}>
      <Stack espacio={2}>
        <Link to="/gastos" className="text-chico text-primario underline-offset-2 hover:underline">
          ← Gastos
        </Link>
        <Row justificar="entre" envolver espacio={3}>
          <Row espacio={2} envolver>
            <h1 className="text-pantalla font-light tracking-[-0.03em]">{g.descripcion}</h1>
            <Badge>{g.tipo}</Badge>
            <Badge tono={TONO_ESTADO[g.estado]}>{ETIQUETA_ESTADO[g.estado]}</Badge>
          </Row>
          {t.veTerreno && (
            <Link to="/terreno" className="text-primario underline-offset-2 hover:underline">
              {t.esAdmin ? "Cargar un pago a cuenta en Terreno" : "Ver Terreno"}
            </Link>
          )}
        </Row>
      </Stack>

      <Card>
        <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-chico">
          <Dato etiqueta="Precio acordado" valor={formatMoneda(g.monto, g.moneda)} />
          <Dato etiqueta="Pagado a cuenta" valor={formatMoneda(g.pagado, g.moneda)} />
          <Dato etiqueta="Falta para la escritura" valor={formatMoneda(g.saldo, g.moneda)} />
          <Dato etiqueta="Escritura" valor={c.confirmada ? `Firmada${c.fecha_escritura ? ` el ${formatFecha(c.fecha_escritura)}` : ""}` : c.fecha_escritura ? `Prevista para el ${formatFecha(c.fecha_escritura)}` : "Sin fecha todavía"} />
          <Dato etiqueta="Rubro" valor={g.rubro.nombre} />
        </dl>
      </Card>

      <Stack espacio={3}>
        <h2 className="text-titulo font-light tracking-tight">Pagos a cuenta</h2>
        {g.estado !== "pagado" && (
          <p className="text-texto-suave">{`Falta pagar ${formatMoneda(g.saldo, g.moneda)} para la escritura. Los pagos a cuenta se cargan desde Terreno.`}</p>
        )}
        <DataList etiqueta="Pagos a cuenta" columnas={columnas} filas={c.pagos} claveFila={(p) => p.gasto_id} alElegirFila={t.verPago} vacio="Todavía no hay pagos a cuenta." />
        {c.pagos.map((p) => (
          <Desplegable key={p.gasto_id} titulo={`Comprobantes de «${p.concepto}» (${p.adjuntos.length})`}>
            <Adjuntos etiqueta={`Comprobantes de «${p.concepto}»`} adjuntos={p.adjuntos} rutaDescarga={t.rutaDescarga} puedeEditar={false} error={null} />
          </Desplegable>
        ))}
      </Stack>
    </Stack>
  );
}
